/**
 * Client-side photo preparation before upload (spec §6.6): decode (incl.
 * HEIC where the browser supports it), honour EXIF orientation, scale to a
 * reasonable size and re-encode as JPEG. Re-encoding also drops metadata
 * such as GPS coordinates. Non-images (PDF) pass through unchanged.
 */
import { translateApiMessage } from "./api-messages.js";
const MAX_EDGE = 2560;
const QUALITY = 0.85;

export async function prepareUpload(file: File): Promise<File> {
  if (!file.type.startsWith("image/") && !/\.(heic|heif)$/i.test(file.name))
    return file;
  if (typeof createImageBitmap !== "function") return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // Browser can't decode it (e.g. HEIC outside Safari): upload as is and
    // let the server validate.
    return file;
  }
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) return file;
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", QUALITY),
  );
  if (!blob) return file;
  // Keep the original if re-encoding made a small PNG/JPEG bigger.
  if (blob.size >= file.size && file.type === "image/jpeg") return file;
  const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg" });
}

/** Upload with progress (fetch has no upload progress events). */
export function uploadWithProgress<T>(
  url: string,
  form: FormData,
  csrfToken: string | null,
  onProgress: (fraction: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", url);
    request.withCredentials = true;
    request.setRequestHeader("Accept", "application/json");
    if (csrfToken) request.setRequestHeader("x-csrf-token", csrfToken);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(request.responseText);
      } catch {
        // ignore
      }
      if (request.status >= 200 && request.status < 300) resolve(body as T);
      else {
        const message =
          body &&
          typeof body === "object" &&
          "error" in body &&
          typeof (body as { error: { message?: unknown } }).error.message ===
            "string"
            ? (body as { error: { message: string } }).error.message
            : request.statusText;
        reject(new Error(translateApiMessage(message)));
      }
    };
    request.onerror = () => reject(new Error("Network request failed"));
    request.send(form);
  });
}
